import { type ApolloCache, type Reference } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

import { None } from 'components/data/helpers';
import {
  createdDate,
  date,
  description,
  getAssetColumn,
  getInspectionTypeColumn,
  getVendorColumn,
  lastDelivered,
  site,
  status,
} from 'components/data/inspections';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlAssetType,
  type AtlasGqlFilterBy,
  type AtlasGqlInspectionListFilter,
  type AtlasGqlReceivedInspectionDelivery,
  AtlasGqlFilterOperator,
  AtlasGqlInspectionStatus,
  useGetInspectionListFiltersQuery,
  useGetInspectionTypesQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { StatusTag } from 'utils/color/helpers';
import { useFeatures } from 'utils/features';
import { startCase } from 'utils/format';
import { getDisplayStatus } from 'utils/inspectionStatus';
import { ENHANCED_INSPECTION_STATUSES, INSPECTION_VENDOR_NAMES } from 'utils/release-toggles';

export type InspectionAssetChangeFailureReason =
  | 'assetHistory'
  | 'damageLinks'
  | 'incompatibleAsset'
  | 'restricted'
  | 'generic';

function parseStoreFieldArguments<T>(storeFieldName: string): T | undefined {
  const argumentsStart = storeFieldName.indexOf('(');
  if (argumentsStart < 0 || !storeFieldName.endsWith(')')) {
    return undefined;
  }

  try {
    return JSON.parse(storeFieldName.slice(argumentsStart + 1, -1)) as T;
  } catch {
    return undefined;
  }
}

export function evictInspectionPicturesFromCache(cache: ApolloCache, inspectionIds: string[]) {
  const movedInspectionIds = new Set(inspectionIds);
  const pictureCacheIds = new Set<string>();
  const collectPictureReferences = (
    cachedPictures: readonly unknown[] | undefined,
    isReference: (value: unknown) => value is Reference
  ) => {
    cachedPictures?.forEach(picture => {
      if (isReference(picture)) {
        pictureCacheIds.add(picture.__ref);
      }
    });
  };

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      queryObservationPictures(cachedPictures, { DELETE, isReference, storeFieldName }) {
        const taskId = parseStoreFieldArguments<{ params?: { taskId?: unknown } }>(storeFieldName)
          ?.params?.taskId;
        if (typeof taskId !== 'string' || !movedInspectionIds.has(taskId)) {
          return cachedPictures;
        }
        collectPictureReferences(cachedPictures, isReference);
        return DELETE;
      },
      getPicturesInArea(cachedResult, { DELETE, isReference, readField, storeFieldName }) {
        const inspectionId = parseStoreFieldArguments<{ inspectionId?: unknown }>(
          storeFieldName
        )?.inspectionId;
        if (typeof inspectionId !== 'string' || !movedInspectionIds.has(inspectionId)) {
          return cachedResult;
        }
        collectPictureReferences(
          readField<readonly unknown[]>('pictures', cachedResult),
          isReference
        );
        return DELETE;
      },
    },
  });

  movedInspectionIds.forEach(inspectionId => {
    const inspectionCacheId = cache.identify({ __typename: 'Inspection', id: inspectionId });
    if (!inspectionCacheId) {
      return;
    }
    cache.modify({
      id: inspectionCacheId,
      fields: {
        pictures(cachedPictures, { DELETE, isReference }) {
          collectPictureReferences(cachedPictures, isReference);
          return DELETE;
        },
      },
    });
  });

  pictureCacheIds.forEach(id => cache.evict({ id }));
  cache.gc();
}

export function getInspectionAssetChangeFailureReason(
  error: unknown
): InspectionAssetChangeFailureReason {
  const errorMessage = (error instanceof Error ? error.message : String(error)).toLowerCase();

  if (errorMessage.includes('blade-swap') || errorMessage.includes('blade swap')) {
    return 'assetHistory';
  }
  if (errorMessage.includes('cannot be moved because damage')) {
    return 'damageLinks';
  }
  if (errorMessage.includes('inspection type') && errorMessage.includes('is not valid for asset')) {
    return 'incompatibleAsset';
  }
  if (errorMessage.includes('skyspecs autonomous drone inspections cannot be moved')) {
    return 'restricted';
  }
  return 'generic';
}

export function useHasInspectionDetailFeature() {
  const { INSPECTION_DETAIL } = useFeatures().features;
  return INSPECTION_DETAIL;
}
function useListFilterModifiedColumns(columns: TTableColumnDef[], hasEnhancedStatuses: boolean) {
  const [columnsWithFilters, setColumnsWithFilters] = useState<TTableColumnDef[]>(columns);
  const { data, error, loading } = useGetInspectionListFiltersQuery();
  const listFilters = data?.inspectionListFilters;

  useEffect(() => {
    if (!loading && !error && listFilters) {
      const columnsWithFilters = columns.map(inspectionColumn => {
        if (!inspectionColumn.dataIndex) {
          return inspectionColumn;
        }

        const filterOptions: (string | AtlasGqlInspectionListFilter)[] | null | undefined = (
          listFilters as any
        )[inspectionColumn.dataIndex];

        const filteredOptions =
          inspectionColumn.dataIndex === 'status' && !hasEnhancedStatuses
            ? filterOptions?.filter(
                v => v === AtlasGqlInspectionStatus.Draft || v === AtlasGqlInspectionStatus.Released
              )
            : filterOptions;

        return {
          ...inspectionColumn,
          filters: filteredOptions?.map(optionValue => {
            // Site and asset filter options come from asset service as objects;
            // inspection filter options come through as plain strings.
            if (typeof optionValue === 'object') {
              // AtlasGqlInspectionListFilterAsset/AtlasGqlInspectionListFilterSite filter option
              return { text: optionValue.name ?? optionValue.id, value: optionValue.id };
            }
            return {
              text:
                inspectionColumn.dataIndex === 'status'
                  ? startCase(optionValue) ?? optionValue
                  : optionValue,
              value: optionValue,
            };
          }),
        };
      });
      setColumnsWithFilters(columnsWithFilters);
    }
  }, [columns, listFilters, error, loading, hasEnhancedStatuses]);

  return columnsWithFilters;
}

export function useServerSideInspectionColumns(
  tableId: string,
  assetIds: string[],
  assetTypes: AtlasGqlAssetType[],
  asset: AtlasGqlAssetType
) {
  const { hasReleaseToggle } = useAccountContext();
  const hasEnhancedStatuses = hasReleaseToggle(ENHANCED_INSPECTION_STATUSES);
  const hasVendorNames = hasReleaseToggle(INSPECTION_VENDOR_NAMES);

  const { data: inspectionTypeData } = useGetInspectionTypesQuery();

  const isSiteDetailsPage = tableId === 'site-inspections-list';

  const statusColumnWithDisplayStatus = useMemo(
    () => ({
      ...status,
      render(_: any, record: any) {
        const rawStatus = record?.status;
        if (!rawStatus) {
          return <None />;
        }
        const displayStatus = getDisplayStatus(rawStatus, hasEnhancedStatuses);
        return (
          <StatusTag
            data-testid="inspection-status"
            status={displayStatus as AtlasGqlInspectionStatus}
          />
        );
      },
    }),
    [hasEnhancedStatuses]
  );

  // Callers pass new `assetIds`/`assetTypes` array refs each render, so key the
  // columns memo on contents — otherwise it recomputes every render and antd's
  // Table infinite-loops ("Maximum update depth exceeded") with a filter open.
  const assetIdsKey = JSON.stringify(assetIds);
  const assetTypesKey = JSON.stringify(assetTypes);

  const columns = useMemo(() => {
    // These columns will appear on fleet page inspection tabs
    if (asset) {
      return [
        date,
        isSiteDetailsPage ? getAssetColumn(tableId, [], []) : undefined,
        getInspectionTypeColumn(inspectionTypeData?.inspectionTypes ?? []),
        description,
        createdDate,
        getVendorColumn(hasVendorNames),
        statusColumnWithDisplayStatus,
        lastDelivered,
      ].filter((c): c is TTableColumnDef => !!c);
    }

    return [
      date,
      site,
      getAssetColumn(tableId, assetTypes, assetIds),
      getInspectionTypeColumn(inspectionTypeData?.inspectionTypes ?? []),
      description,
      createdDate,
      getVendorColumn(hasVendorNames),
      statusColumnWithDisplayStatus,
      lastDelivered,
    ];
    // assetIds/assetTypes are intentionally keyed by content (assetIdsKey/assetTypesKey) above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableId,
    asset,
    isSiteDetailsPage,
    assetTypesKey,
    assetIdsKey,
    inspectionTypeData,
    hasVendorNames,
    statusColumnWithDisplayStatus,
  ]);

  return useListFilterModifiedColumns(columns, hasEnhancedStatuses);
}

export const generateTableFilters = ({
  siteIds,
  assetIds,
}: {
  siteIds?: string[];
  assetIds?: string[];
}): AtlasGqlFilterBy[] => {
  const initialFilter: AtlasGqlFilterBy[] = [];

  if (siteIds?.length) {
    initialFilter.push({
      key: 'site',
      operator: AtlasGqlFilterOperator.Equals,
      values: siteIds,
    });
  }

  if (assetIds?.length) {
    initialFilter.push({
      key: 'assetOrComponentId',
      operator: AtlasGqlFilterOperator.Equals,
      values: assetIds,
    });
  }

  return initialFilter;
};

export const receivedInspectionDeliveriesLatestCreated = (
  deliveries: AtlasGqlReceivedInspectionDelivery[]
) =>
  deliveries
    .map((d: any) => d.created)
    .filter((c: string): c is string => Boolean(c))
    .sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime())[0];
